// src/screens/CommunityPlays.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import Select, { MultiValue } from "react-select";
import { Target, PlusCircle, Globe, ArrowLeft, ArrowRight } from "lucide-react";
import { useTheme } from "../context/ThemeProvider";
import i18n from "../i18n";
import { LogoLoader } from "../components/LogoLoader";
import { BackButton } from "../components/BackButton";
import { ReportBlockMenu } from "../components/ReportBlockMenu";
import { getBlockedUserIds } from "../lib/moderation";


type Tag = { id: string; name: string };
type Option = { value: string; label: string };

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const CommunityPlays = () => {
  const { t, lng } = useI18nSafe();
  const locale = lng === "fr" ? "fr-FR" : "en-US";

  const [plays, setPlays] = useState<any[]>([]);
  const [filteredPlays, setFilteredPlays] = useState<any[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const playsPerPage = 20;

  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const removePlaysByAuthor = (authorId: string) => {
    setPlays((prev) => prev.filter((p) => p.user_id !== authorId));
    setFilteredPlays((prev) => prev.filter((p) => p.user_id !== authorId));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // 👈 pour afficher le loader pendant le fetch

      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id ?? null);

      const [tagsRes, playsRes, blockedIds] = await Promise.all([
        supabase.from("play_tags").select("*"),
        supabase
          .from("plays")
          .select(`*, user_profiles ( full_name )`)
          .eq("is_public", true)
          .order("created_at", { ascending: false }),
        getBlockedUserIds(),
      ]);

      setTags(tagsRes.data || []);

      if (playsRes.error) {
        console.error("Erreur récupération plays publics:", playsRes.error.message);
      } else {
        const visible = (playsRes.data || []).filter(
          (p) => !blockedIds.has(p.user_id)
        );
        setPlays(visible);
        setFilteredPlays(visible);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const filtered = plays.filter((play) => {
      const matchName = (play.name || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchTags =
        selectedTags.length === 0 ||
        selectedTags.every((tagId) => play.tag_ids?.includes(tagId));
      return matchName && matchTags;
    });
    setFilteredPlays(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedTags, plays]);

  const handleView = (playId: string) => navigate(`/play/${playId}`);
  const resetFilters = () => { setSearchTerm(""); setSelectedTags([]); };

  // Pagination
  const indexOfLastPlay = currentPage * playsPerPage;
  const indexOfFirstPlay = indexOfLastPlay - playsPerPage;
  const currentPlays = filteredPlays.slice(indexOfFirstPlay, indexOfLastPlay);
  const totalPages = Math.ceil(filteredPlays.length / playsPerPage);

  // Styles react-select selon thème
  const selectStyles = useMemo(() => ({
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: isDark ? "#111827" : "#ffffff",
      borderColor: isDark ? (state.isFocused ? "#4b5563" : "#374151") : (state.isFocused ? "#9ca3af" : "#d1d5db"),
      boxShadow: "none",
      ":hover": { borderColor: isDark ? "#4b5563" : "#9ca3af" },
      color: isDark ? "#e5e7eb" : "#111827",
    }),
    menu: (base: any) => ({ ...base, backgroundColor: isDark ? "#111827" : "#ffffff", color: isDark ? "#e5e7eb" : "#111827", zIndex: 30 }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? (isDark ? "#1f2937" : "#f3f4f6") : "transparent",
      color: isDark ? "#e5e7eb" : "#111827",
    }),
    multiValue: (base: any) => ({ ...base, backgroundColor: isDark ? "#1f2937" : "#dbeafe" }),
    multiValueLabel: (base: any) => ({ ...base, color: isDark ? "#93c5fd" : "#1e3a8a" }),
    multiValueRemove: (base: any) => ({ ...base, ":hover": { backgroundColor: isDark ? "#374151" : "#bfdbfe" } }),
    placeholder: (base: any) => ({ ...base, color: isDark ? "#9ca3af" : "#6b7280" }),
    input: (base: any) => ({ ...base, color: isDark ? "#e5e7eb" : "#111827" }),
    singleValue: (base: any) => ({ ...base, color: isDark ? "#e5e7eb" : "#111827" }),
  }), [isDark]);

  // 🔥 Loader SwishData pendant le chargement
  if (loading) {
    return <LogoLoader />;
  }

  return (
  <div className="min-h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
    {/* ===== HEADER STICKY ===== */}
    <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-gray-950/85 backdrop-blur border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-6xl mx-auto px-4 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] flex items-center gap-3">
        <BackButton fallback="/dashboard" />

        <h1 className="text-xl sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
          {t("communityplays:title")}
        </h1>
      </div>
    </div>

    {/* ===== CONTENU ===== */}
    <div className="max-w-6xl mx-auto px-4 py-4">


      {/* Actions haut */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <button
          onClick={() => navigate("/playcreator")}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-full
                     focus:outline-none focus:ring-2 focus:ring-blue-400/60"
        >
          <PlusCircle className="h-5 w-5" />
          {t("communityplays:actions.create")}
        </button>

        <button
          onClick={() => navigate("/complays")}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-full
                     focus:outline-none focus:ring-2 focus:ring-green-400/60"
        >
          <Globe className="h-5 w-5" />
          {t("communityplays:actions.view_comm")}
        </button>
      </div>

      {/* Recherche + reset */}
      <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
        <input
          type="text"
          placeholder={t("communityplays:search.placeholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border px-3 py-2 rounded w-full md:w-1/2
                     bg-white border-gray-300 text-gray-900 placeholder-gray-400
                     focus:outline-none focus:ring-2 focus:ring-orange-400/60
                     dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
        />

        <button
          onClick={resetFilters}
          className="px-4 py-2 rounded w-full md:w-fit
                     bg-gray-200 hover:bg-gray-300 text-gray-800
                     dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-100
                     focus:outline-none focus:ring-2 focus:ring-gray-400/40"
        >
          {t("communityplays:filters.reset")}
        </button>
      </div>

      {/* Sélecteur multiselect tags */}
      <div className="mb-6">
        <label className="block mb-2 font-medium flex items-center gap-2">
          <Target className="w-4 h-4" />
          {t("communityplays:filters.tag_label")}
        </label>
        <div className="md:w-1/2">
          <Select<Option, true>
            isMulti
            options={tags.map((tag) => ({ value: tag.id, label: tag.name }))}
            value={tags
              .filter((tag) => selectedTags.includes(tag.id))
              .map((tag) => ({ value: tag.id, label: tag.name }))}
            onChange={(selected: MultiValue<Option>) => {
              const values = selected.map((o) => o.value);
              setSelectedTags(values);
            }}
            styles={selectStyles}
            placeholder={t("communityplays:filters.tag_placeholder")}
            classNamePrefix="react-select"
          />
        </div>
      </div>

      {/* Résultats */}
      {currentPlays.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">
          {t("communityplays:empty.no_match")}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentPlays.map((play) => (
            <div
              key={play.id}
              className="relative rounded-2xl border bg-white border-gray-200 shadow-sm p-4
                         dark:bg-gray-900 dark:border-gray-800"
            >
              <ReportBlockMenu
                className="absolute top-3 right-3"
                contentType="play"
                contentId={play.id}
                authorId={play.user_id}
                authorName={play.user_profiles?.full_name}
                currentUserId={currentUserId}
                onBlocked={removePlaysByAuthor}
              />

              <h2 className="pr-8 text-lg font-semibold text-gray-900 dark:text-gray-100">{play.name}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("communityplays:result.created_at")}{" "}
                {new Date(play.created_at).toLocaleString(locale)}
              </p>

              {play.user_profiles?.full_name && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t("communityplays:result.author")} {play.user_profiles.full_name}
                </p>
              )}

              {play.tag_ids && play.tag_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags
                    .filter((tag) => play.tag_ids.includes(tag.id))
                    .map((tag) => (
                      <span
                        key={tag.id}
                        className="px-2 py-1 text-xs rounded-full
                                   bg-blue-100 text-blue-800
                                   dark:bg-blue-900/40 dark:text-blue-300"
                      >
                        {tag.name}
                      </span>
                    ))}
                </div>
              )}

              <button
                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded
                           focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                onClick={() => handleView(play.id)}
              >
                {t("communityplays:result.view_play")}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded
              ${currentPage === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500"
                : "bg-blue-600 text-white hover:bg-blue-700"}`}
          >
            <ArrowLeft className="w-4 h-4" />
            {t("communityplays:pagination.prev")}
          </button>

          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t("communityplays:pagination.page_of", { page: currentPage, total: totalPages })}
          </span>

          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded
              ${currentPage === totalPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500"
                : "bg-blue-600 text-white hover:bg-blue-700"}`}
          >
            {t("communityplays:pagination.next")}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
      </div>
    </div>
    
  );
};
